import type { ReactNode } from "react";

import { EmptyState } from "./EmptyState";

export interface Column<T> {
  readonly key: string;
  readonly header: string;
  readonly align?: "left" | "right";
  readonly render: (row: T) => ReactNode;
}

export interface DataTableProps<T> {
  readonly columns: readonly Column<T>[];
  readonly rows: readonly T[];
  readonly rowKey: (row: T) => string;
  readonly onRowClick?: (row: T) => void;
  readonly emptyTitle?: string;
  readonly emptyMessage?: string;
}

// Generic dense table. Numeric columns right-align via `align: "right"` (.num class). Optional
// row-click for drill-through. Renders an explicit empty state rather than an empty <tbody>.
export const DataTable = <T,>({
  columns,
  rows,
  rowKey,
  onRowClick,
  emptyTitle = "No rows",
  emptyMessage,
}: DataTableProps<T>): ReactNode => {
  if (rows.length === 0) {
    return <EmptyState title={emptyTitle} message={emptyMessage} />;
  }
  return (
    <div style={{ overflowX: "auto" }}>
      <table className="data-table">
        <thead>
          <tr>
            {columns.map((col) => (
              <th key={col.key} className={col.align === "right" ? "num" : undefined}>
                {col.header}
              </th>
            ))}
          </tr>
        </thead>
        <tbody>
          {rows.map((row) => (
            <tr
              key={rowKey(row)}
              onClick={onRowClick ? () => onRowClick(row) : undefined}
              style={onRowClick ? { cursor: "pointer" } : undefined}
            >
              {columns.map((col) => (
                <td key={col.key} className={col.align === "right" ? "num" : undefined}>
                  {col.render(row)}
                </td>
              ))}
            </tr>
          ))}
        </tbody>
      </table>
    </div>
  );
};
